// v4 (spec-v4-structure-and-stage.md 5.2, decision E): one pattern for details. A summary row (title, a one-line
// summary, an optional count and a chevron) that opens inline; no modals for details. Open/closed is remembered per
// fold id (localStorage, fold.ts).
//
// Never folded: a price, an error, a blocker, the review count. Pass them as `price` (shown on the summary row) and
// `notices` (shown above the row, from components/Notice.tsx); they stay visible whether the fold is open or not.
import { useEffect, useId, useState, type ReactNode } from "react";
import { FOLD_EVENT, foldBodyId, readFold, writeFold, type FoldEventDetail, type FoldStore } from "../fold";
import type { NoticeData } from "../nextstep";
import { Notice } from "./Notice";

export type FoldProps = {
  /** remembered per id: "scene.sounds", "brief.look" */
  id: string;
  title: string;
  /** one line, cut with an ellipsis ("Kling 2.5 · 85") */
  summary?: string;
  count?: number;
  /** a paid thing's price: on the summary row, never inside */
  price?: number | null;
  /** errors and blockers: above the row, never inside */
  notices?: NoticeData[];
  defaultOpen?: boolean;
  /**
   * v4 (V4-P2): the how_do_i targets inside this fold ("settings.spending", "#mem-h"). A highlight that can't find its
   * target opens the fold that holds it first (highlight.ts), so a folded control can still be pointed at.
   */
  holds?: string[];
  /** tests and previews: where to remember (default localStorage) */
  store?: FoldStore | null;
  className?: string;
  children?: ReactNode;
};

export function Fold({ id, title, summary, count, price, notices, defaultOpen = false, holds, store, className = "", children }: FoldProps) {
  const [open, setOpen] = useState(() => readFold(id, defaultOpen, store));
  const bodyId = `${foldBodyId(id)}-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  // the Director opens or closes folds by id (fold.ts setFold)
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<FoldEventDetail>).detail;
      if (d?.id === id) setOpen(d.open);
    };
    window.addEventListener(FOLD_EVENT, on);
    return () => window.removeEventListener(FOLD_EVENT, on);
  }, [id]);
  const toggle = () => {
    setOpen(!open);
    writeFold(id, !open, store);
  };
  return (
    <section className={`fold${open ? " is-open" : ""}${className ? ` ${className}` : ""}`} data-fold={id} data-holds={holds?.length ? holds.join(" ") : undefined}>
      {notices?.map((n, i) => <Notice key={`${n.code ?? n.tone}-${i}`} {...n} />)}
      <button data-action="fold" type="button" className="fold-row" aria-expanded={open} aria-controls={bodyId} onClick={toggle}>
        <svg className="fold-chev" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
          <path d="m9 6 6 6-6 6" />
        </svg>
        <span className="fold-title">{title}</span>
        {count !== undefined && <span className="fold-count">{count}</span>}
        {summary && <span className="fold-summary">{summary}</span>}
        {price != null && price > 0 && <span className="fold-price">{price} credits</span>}
      </button>
      {open && (
        <div className="fold-body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}
